import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { V, Haptic } from '../../utils/theme';
import { LS } from '../../utils/storage';
import { Icons } from '../../components/Icons';
import { Card, Btn, Field, Sheet, Chip, Stat, Progress, Skeleton, SkeletonCard, MsgBannerCtrl, SuccessToastCtrl, ConfirmCtrl } from '../../components/ui';
import { today, ago, fmtShort, fmtFull, uid, friendDisplayName, convW, wUnit, calc1RM, calcStreak } from '../../utils/helpers';
import { SocialAPI, SYNC_URL } from '../../utils/sync';
import { ShareCard } from '../../utils/share';
import { BADGE_DEFS, calcEarnedBadges } from '../../data/badges';
import { useLayout } from '../../utils/responsive';
import { IRON_RANKS } from '../../data/ranks';
import { HelpBtn, compressImage } from '../features';
import { IronScoreCard, RankBadge, DailyMissionsCard } from '../gamification';
import { useStreak, calcReadiness } from '../../components/dialogs';
import { SectionGrid } from '../hubs';
import { MS_PER_MINUTE, MS_PER_HOUR, MS_PER_DAY, ONLINE_THRESHOLD_MS } from '../../utils/constants';
import { apiPost } from '../../utils/api';
import { OnlineIndicator } from './shared';
import { track } from '../../components/kit';
import { subscribeDMs } from '../../utils/realtime';
import { ensureMessageAccount, readMessageSnapshot } from '../../utils/messagePolling';
import { mergeThread, applySendAck, parseSendResult, buildPendingMessage, recoverInterruptedMessages } from './dmThread';

// Survives navigation, but clears on reload so interrupted sends become retryable.
const activeSends=new Set();
const sendKey=(email,friend,id)=>JSON.stringify([email,friend,id]);

export function MessagesTab({s,d}){
  const { isDesktop } = useLayout();
  const email=s.profile?.email;
  ensureMessageAccount(LS,Object.keys(localStorage),email);
  const displayName=s.profile?.firstName||email?.split("@")[0]||"You";
  const [friends,setFriends]=useState(null);
  const [activeChat,setActiveChat]=useState(null); // friend object or null (inbox)
  const [search,setSearch]=useState("");
  const [loading,setLoading]=useState(!!email);
  const [tick,setTick]=useState(0); // forces re-render to pick up LS unread changes
  const friendsReqRef=useRef(0);

  const refreshFriends=useCallback((showLoading)=>{
    if(!email){setFriends(null);setLoading(false);return;}
    const req=++friendsReqRef.current;
    if(showLoading)setLoading(true);
    SocialAPI.getFriends(email).then(r=>{
      if(req!==friendsReqRef.current)return;
      if(r)setFriends(r);
      setLoading(false);
    }).catch(()=>{if(req!==friendsReqRef.current)return;setLoading(false);});
  },[email]);

  useEffect(()=>{
    setFriends(null);setActiveChat(null);
    if(!email){setLoading(false);return;}
    refreshFriends(true);
    return()=>{friendsReqRef.current++;};
  },[email,refreshFriends]);

  // Re-render inbox every 30s — gated on tab visibility so background tabs don't tick
  useEffect(()=>{
    let iv=null;
    const start=()=>{ if(iv)return; iv=setInterval(()=>setTick(t=>t+1),30000); };
    const stop=()=>{ if(iv){clearInterval(iv);iv=null;} };
    const visHandler=()=>{ document.visibilityState==="visible"?start():stop(); };
    visHandler();
    document.addEventListener("visibilitychange",visHandler);
    return()=>{ stop(); document.removeEventListener("visibilitychange",visHandler); };
  },[]);

  // Auto-open from push notification
  useEffect(()=>{
    const fromEmail=LS.get("ft-open-dm-from");
    if(fromEmail&&friends?.friends){
      const f=friends.friends.find(fr=>fr.email===fromEmail);
      if(f){setActiveChat(f);}
      // Always clear after attempting — prevents stale auto-opens on next launch
      LS.set("ft-open-dm-from",null);
    }
  },[friends]);

  if(activeChat){
    return <IMConversation key={`${email}:${activeChat.email}`}
      s={s} email={email} displayName={displayName}
      friend={activeChat}
      onBack={()=>{setActiveChat(null);refreshFriends(false);}}
    />;
  }

  // Build inbox rows — sorted by last message time desc
  const friendList=(friends?.friends||[]).filter(f=>{
    if(!search)return true;
    const q=search.toLowerCase();
    return (f.name||"").toLowerCase().includes(q)||(f.username||"").toLowerCase().includes(q);
  });
  const getLastMsg=(f)=>{ const cached=LS.get(`ft-dm-${f.email}`)||[]; return cached[cached.length-1]||null; };
  const getUnread=(f)=>parseInt(LS.get(`ft-unread-${f.email}`))||0;
  const sorted=[...friendList].sort((a,b)=>{
    const at=getLastMsg(a)?.ts||"0"; const bt=getLastMsg(b)?.ts||"0";
    return bt.localeCompare(at);
  });
  const pending=friends?.incoming||[];

  // Format timestamp for inbox row (iMessage style)
  const fmtInboxTime=(ts)=>{
    if(!ts)return"";
    const d2=new Date(ts),now=new Date();
    const diff=(now-d2)/1000;
    if(diff<60)return"now";
    if(diff<3600)return`${Math.floor(diff/60)}m`;
    // Fix 9: use calendar day comparison, not 24h window
    const todayStr=now.toDateString();
    const yesterdayStr=new Date(now-86400000).toDateString();
    const msgDayStr=d2.toDateString();
    if(msgDayStr===todayStr)return d2.toLocaleTimeString([],{hour:"numeric",minute:"2-digit"});
    if(msgDayStr===yesterdayStr)return"Yesterday";
    if(diff<604800)return d2.toLocaleDateString([],{weekday:"short"});
    return d2.toLocaleDateString([],{month:"numeric",day:"numeric"});
  };

  return(
    <div style={{display:"flex",flexDirection:"column",height:"calc(var(--content-h, 100vh - 120px))"}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"4px 0 12px"}}>
        <button onClick={()=>d({type:"TAB",tab:"social"})} style={{background:"none",border:"none",display:"flex",alignItems:"center",gap:4,cursor:"pointer",padding:0}}>
          <svg width="9" height="16" viewBox="0 0 9 16" fill="none"><path d="M8 1L1 8L8 15" stroke={V.accent} strokeWidth="2.2" strokeLinecap="round"/></svg>
          <span style={{fontSize:14,color:V.accent,fontWeight:600}}>Social</span>
        </button>
        <div style={{fontSize:17,fontWeight:800,color:V.text}}>Messages</div>
        <button onClick={()=>d({type:"TAB",tab:"social_friends"})} style={{background:"none",border:"none",cursor:"pointer",padding:"4px 8px"}}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={V.accent} strokeWidth="2" strokeLinecap="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
        </button>
      </div>

      {/* Search bar */}
      <div style={{position:"relative",marginBottom:10}}>
        <div style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={V.text3} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        </div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search"
          style={{width:"100%",padding:"9px 12px 9px 34px",background:V.hoverBg,
            border:"none",borderRadius:12,color:V.text,fontSize:14,outline:"none",fontFamily:V.font,boxSizing:"border-box"}}/>
        {search&&<button onClick={()=>setSearch("")} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",
          background:track(),border:"none",borderRadius:10,width:18,height:18,cursor:"pointer",
          display:"flex",alignItems:"center",justifyContent:"center",color:V.text3,fontSize:11}}>✕</button>}
      </div>

      {/* Pending requests banner */}
      {pending.length>0&&(
        <button onClick={()=>d({type:"TAB",tab:"social_friends"})} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",
          background:`${V.accent}08`,border:`1px solid ${V.accent}20`,borderRadius:12,cursor:"pointer",marginBottom:8,width:"100%",textAlign:"left"}}>
          <div style={{width:36,height:36,borderRadius:18,background:V.accent,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <span style={{fontSize:16}}>👥</span>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:V.accent}}>{pending.length} friend request{pending.length>1?"s":""}</div>
            <div style={{fontSize:11,color:V.text3}}>Tap to review</div>
          </div>
          <svg width="8" height="14" viewBox="0 0 8 14" fill="none"><path d="M1 1l6 6-6 6" stroke={V.text3} strokeWidth="1.8" strokeLinecap="round"/></svg>
        </button>
      )}

      {/* Conversation list */}
      <div style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch"}}>
        {loading&&[0,1,2,3].map(i=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:`1px solid ${V.cardBorder}`}}>
            <div style={{width:52,height:52,borderRadius:26,background:V.hoverBg,flexShrink:0}}/>
            <div style={{flex:1}}>
              <div style={{height:13,width:"40%",borderRadius:6,background:V.hoverBg,marginBottom:6}}/>
              <div style={{height:11,width:"70%",borderRadius:6,background:V.inputBg}}/>
            </div>
          </div>
        ))}
        {!loading&&sorted.length===0&&(
          <div style={{textAlign:"center",padding:"60px 20px"}}>
            <div style={{fontSize:48,marginBottom:12}}>💬</div>
            <div style={{fontSize:16,fontWeight:700,color:V.text,marginBottom:6}}>No Messages Yet</div>
            <div style={{fontSize:13,color:V.text3,lineHeight:1.6}}>Add friends to start chatting</div>
            <button onClick={()=>d({type:"TAB",tab:"social_friends"})} style={{marginTop:16,padding:"10px 24px",
              background:V.accent,border:"none",borderRadius:20,cursor:"pointer",fontSize:13,fontWeight:700,color:V.bg,fontFamily:V.font}}>
              Add Friends
            </button>
          </div>
        )}
        {sorted.map((f,i)=>{
          const last=getLastMsg(f);
          const unread=getUnread(f);
          const isOwn=last?.isOwn||last?.from===email;
          let preview=last?.text||"";
          if(last?.imageData)preview="📷 Photo";
          if(preview.length>32)preview=preview.slice(0,32)+"…";
          return(
          <button key={f.email} onClick={()=>setActiveChat(f)}
            style={{display:"flex",alignItems:"center",gap:12,width:"100%",padding:"10px 0",
              background:"none",border:"none",cursor:"pointer",textAlign:"left",WebkitTapHighlightColor:"transparent",
              borderBottom:`1px solid ${V.cardBorder}`}}>
            {/* Avatar */}
            <div style={{position:"relative",flexShrink:0}}>
              <div style={{width:52,height:52,borderRadius:26,overflow:"hidden",
                background:f.avatar?"none":`linear-gradient(135deg,${V.purple},#ec4899)`,
                display:"flex",alignItems:"center",justifyContent:"center"}}>
                {f.avatar
                  ?<img src={f.avatar} style={{width:"100%",height:"100%",objectFit:"cover"}} alt=""/>
                  :<span style={{fontSize:20,color:"#fff",fontWeight:800}}>{(f.name||"?")[0].toUpperCase()}</span>}
              </div>
              {unread>0&&<div style={{position:"absolute",bottom:0,right:0,width:18,height:18,borderRadius:9,
                background:V.accent,border:`2px solid ${V.bg}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <span style={{fontSize:11,fontWeight:800,color:V.bg}}>{unread>9?"9+":unread}</span>
              </div>}
            </div>
            {/* Content */}
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:2}}>
                <span style={{fontSize:15,fontWeight:unread>0?700:500,color:V.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"60%"}}>{friendDisplayName(f)}</span>
                <span style={{fontSize:11,color:unread>0?V.accent:V.text3,fontWeight:unread>0?600:400,flexShrink:0,marginLeft:4}}>{fmtInboxTime(last?.ts)}</span>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:3,minWidth:0}}>
                {isOwn&&last&&<span style={{fontSize:13,color:V.text3,flexShrink:0}}>You: </span>}
                <span style={{fontSize:13,color:unread>0?V.text2:V.text3,fontWeight:unread>0?500:400,
                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{preview||"Tap to chat"}</span>
              </div>
            </div>
            <svg width="8" height="14" viewBox="0 0 8 14" fill="none" style={{flexShrink:0}}>
              <path d="M1 1l6 6-6 6" stroke={V.text3} strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── iMessage-style conversation view ───
export function IMConversation({s,email,displayName,friend,onBack}){
  const [messages,setMessagesState]=useState(()=>{
    ensureMessageAccount(LS,Object.keys(localStorage),email);
    const cached=LS.get(`ft-dm-${friend.email}`);
    const rows=Array.isArray(cached)?cached:[];
    const activeIds=rows.filter(m=>activeSends.has(sendKey(email,friend.email,m.localId||m.id))).map(m=>m.localId||m.id);
    return recoverInterruptedMessages(rows,activeIds);
  });
  const messagesRef=useRef(messages);
  const mountedRef=useRef(true);
  const setMessages=useCallback(update=>{
    const next=typeof update==="function"?update(messagesRef.current):update;
    messagesRef.current=next;
    if(mountedRef.current)setMessagesState(next);
  },[]);
  const accountIsCurrent=()=>LS.get("ft-profile")?.email===email;
  const [input,setInput]=useState("");
  const [imgPreview,setImgPreview]=useState(null);
  const [loading,setLoading]=useState(true);
  const [loadError,setLoadError]=useState(false);
  const [hasMore,setHasMore]=useState(false);
  const [oldestCursor,setOldestCursor]=useState(null);
  const [loadingOlder,setLoadingOlder]=useState(false);
  const [reacting,setReacting]=useState(null); // message id being reacted to
  const [reactions,setReactions]=useState(()=>LS.get(`ft-reactions-${friend.email}`)||{});
  const [searchOpen,setSearchOpen]=useState(false);
  const [searchQ,setSearchQ]=useState("");
  const [friendInfo,setFriendInfo]=useState(false); // friend info sheet open
  const endRef=useRef(null);
  const inputRef=useRef(null);
  const imgRef=useRef(null);
  const listRef=useRef(null);
  const lastMsgTsRef=useRef(null);
  const lastInboundTsRef=useRef(null);
  const friendLastReadRef=useRef(null);
  const loadGenRef=useRef(0);
  const composerLockRef=useRef(false);
  const dmPollRef=useRef(null);
  const pullLatestRef=useRef(null);

  const persistThread=(friendEmail,msgs)=>{
    if(!accountIsCurrent())return;
    const stored=LS.get(`ft-dm-${friendEmail}`);
    const confirmedLocals=new Set(msgs.filter(m=>!m.local&&m.localId).map(m=>String(m.localId)));
    const cached=(Array.isArray(stored)?stored:[]).filter(m=>!confirmedLocals.has(String(m.id)));
    const combined=[...new Map([...cached,...msgs].map(m=>[String(m.id),m])).values()].sort((a,b)=>Date.parse(a.ts)-Date.parse(b.ts));
    const keep=[...combined.filter(m=>m.local||m.pending||m.failed||m.status==="pending"||m.status==="failed"),...combined.slice(-200)];
    LS.set(`ft-dm-${friendEmail}`,[...new Map(keep.map(m=>[String(m.id),m])).values()].sort((a,b)=>Date.parse(a.ts)-Date.parse(b.ts)));
  };
  const markVisibleRead=rows=>{
    if(!mountedRef.current||!accountIsCurrent()||document.visibilityState!=="visible")return;
    const readAt=readMessageSnapshot(LS,email,friend.email,rows);
    if(readAt)SocialAPI.markDMsRead(email,friend.email,readAt);
  };

  // Load from server on mount — merge into local pending/failed, ignore stale responses
  const loadMessages=useCallback(()=>{
    const myEmail=email,friendEmail=friend.email;
    const gen=++loadGenRef.current;
    if(!myEmail||!friendEmail){setLoading(false);return;}
    setLoading(true);setLoadError(false);
    SocialAPI.getDMs(myEmail,friendEmail).then(r=>{
      if(gen!==loadGenRef.current||!accountIsCurrent())return;
      if(r&&Array.isArray(r.messages)){
        if("friendLastRead" in r)friendLastReadRef.current=r.friendLastRead||null;
        setMessages(prev=>{
          const merged=mergeThread(prev,r.messages,{email:myEmail,friendLastRead:friendLastReadRef.current,cap:0,mode:"latest"});
          persistThread(friendEmail,merged);
          lastMsgTsRef.current=merged.slice(-1)[0]?.ts||null;
          return merged;
        });
        setHasMore(!!r.hasMore);
        setOldestCursor(r.hasMore&&r.nextCursor?r.nextCursor:null);
        setLoadError(false);
        markVisibleRead(r.messages);
      } else {
        setMessages(prev=>{
          if(prev.length)return prev;
          const cached=LS.get(`ft-dm-${friendEmail}`)||[];
          if(cached.length){lastMsgTsRef.current=cached.slice(-1)[0]?.ts||null;return cached;}
          return prev;
        });
        setLoadError(true);
      }
      setLoading(false);
    }).catch(()=>{
      if(gen!==loadGenRef.current||!accountIsCurrent())return;
      setLoadError(true);setLoading(false);
    });
  },[email,friend.email]);
  useEffect(()=>{mountedRef.current=true;loadMessages();return()=>{mountedRef.current=false;loadGenRef.current++;};},[loadMessages]);

  // Bug 10: scroll after messages render, not on a fixed timer
  const didInitScroll=useRef(false);
  useEffect(()=>{
    if(!loading&&!didInitScroll.current){
      didInitScroll.current=true;
      endRef.current?.scrollIntoView({behavior:"instant"});
    }
  },[loading,messages.length]);

  const scrollToBottom=(smooth)=>requestAnimationFrame(()=>endRef.current?.scrollIntoView({behavior:smooth?"smooth":"instant"}));

  const loadOlder=()=>{
    if(!oldestCursor||loadingOlder)return;
    setLoadingOlder(true);
    const list=listRef.current;
    const prevScrollHeight=list?list.scrollHeight:0;
    const prevScrollTop=list?list.scrollTop:0;
    const myEmail=email,friendEmail=friend.email,cursor=oldestCursor;
    const gen=loadGenRef.current;
    SocialAPI.getDMs(myEmail,friendEmail,cursor).then(r=>{
      if(gen!==loadGenRef.current||!accountIsCurrent())return;
      if(r&&Array.isArray(r.messages)){
        if("friendLastRead" in r && r.friendLastRead)friendLastReadRef.current=r.friendLastRead;
        setMessages(prev=>{
          const merged=mergeThread(prev,r.messages,{email:myEmail,friendLastRead:friendLastReadRef.current,cap:0,mode:"older"});
          persistThread(friendEmail,merged);
          return merged;
        });
        setHasMore(!!r.hasMore);
        setOldestCursor(r.hasMore&&r.nextCursor?r.nextCursor:null);
        requestAnimationFrame(()=>{
          if(list)list.scrollTop=prevScrollTop+list.scrollHeight-prevScrollHeight;
        });
      }
      setLoadingOlder(false);
    }).catch(()=>{if(gen===loadGenRef.current)setLoadingOlder(false);});
  };

  // Poll for new messages / receipts every 8s while in conversation.
  // Receipts must refresh even when the newest timestamp is unchanged.
  useEffect(()=>{
    let cancelled=false;
    let polling=false;
    const startPoll=()=>{
      if(dmPollRef.current)clearInterval(dmPollRef.current);
      const rate=document.visibilityState==="visible"?8000:90000;
      const pullLatest=()=>{
        const myEmail=email,friendEmail=friend.email;
        if(!myEmail||!friendEmail||polling||!accountIsCurrent()||document.visibilityState!=="visible")return;
        polling=true;
        SocialAPI.getDMs(myEmail,friendEmail).then(r=>{
          if(cancelled||!accountIsCurrent())return;
          if(!r||!Array.isArray(r.messages))return;
          if("friendLastRead" in r)friendLastReadRef.current=r.friendLastRead||null;
          setMessages(prev=>{
            const merged=mergeThread(prev,r.messages,{email:myEmail,friendLastRead:friendLastReadRef.current,cap:0,mode:"latest"});
            persistThread(friendEmail,merged);
            const prevLast=prev.slice(-1)[0];
            const nextLast=merged.slice(-1)[0];
            const newestChanged=(nextLast?.id!==prevLast?.id)||(nextLast?.ts!==prevLast?.ts);
            lastMsgTsRef.current=nextLast?.ts||lastMsgTsRef.current;
            if(newestChanged){
              const list=listRef.current;
              if(list){const nearBottom=list.scrollHeight-list.scrollTop-list.clientHeight<80;if(nearBottom)scrollToBottom(true);}
            }
            return merged;
          });
          markVisibleRead(r.messages);
        }).catch(()=>{}).finally(()=>{polling=false;});
      };
      pullLatestRef.current=pullLatest;
      dmPollRef.current=setInterval(pullLatest,rate);
    };
    startPoll();
    const dmVisHandler=()=>{startPoll();if(document.visibilityState==="visible")pullLatestRef.current?.();};
    document.addEventListener("visibilitychange",dmVisHandler);
    const unsubscribe=subscribeDMs(email,(ping)=>{if(!ping?.from||ping.from===friend.email)pullLatestRef.current?.();});
    return()=>{cancelled=true;clearInterval(dmPollRef.current);document.removeEventListener("visibilitychange",dmVisHandler);unsubscribe();};
  },[email,friend.email]);

  const handleImg=async(e)=>{
    const file=e.target.files?.[0];if(!file)return;
    const c=await compressImage(file,600,0.72);
    if(c)setImgPreview(c);
    e.target.value="";
  };

  const finishSend=(localId,result)=>{
    activeSends.delete(sendKey(email,friend.email,localId));
    if(!accountIsCurrent())return;
    const parsed=parseSendResult(result);
    setMessages(prev=>{
      // A global poll can update this cache after the conversation unmounts.
      const cached=LS.get(`ft-dm-${friend.email}`);
      const combined=[...new Map([...prev,...(Array.isArray(cached)?cached:[])].map(m=>[String(m.id),m])).values()];
      const next=applySendAck(combined,localId,result);
      persistThread(friend.email,next);
      const last=next.slice(-1)[0];
      if(last?.ts)lastMsgTsRef.current=last.ts;
      return next;
    });
    window.dispatchEvent(new CustomEvent("ironlog:dm-ack",{detail:{email,friend:friend.email,localId,result}}));
    if(parsed.ok){
      // log_event owns recipient notifications; a second notify_dm duplicates pushes.
      LS.set("ft-dm-sent-today",today());
    }
  };

  // A send may finish after leaving and reopening this thread. Deliver the
  // acknowledgement to the currently mounted instance as well as the cache.
  useEffect(()=>{
    const receive=event=>{
      const ack=event.detail;
      if(!ack||ack.email!==email||ack.friend!==friend.email||!accountIsCurrent())return;
      setMessages(prev=>applySendAck(prev,ack.localId,ack.result));
    };
    window.addEventListener("ironlog:dm-ack",receive);
    return()=>window.removeEventListener("ironlog:dm-ack",receive);
  },[email,friend.email,setMessages]);

  const enqueueSend=({text,imageData})=>{
    if(!email||!friend.email||!accountIsCurrent())return;
    const now=new Date().toISOString();
    const id=uid();
    const msg=buildPendingMessage({id,email,name:displayName,text:imageData?(text||"📷 Photo"):text,imageData,ts:now,baselineServerIDs:messagesRef.current.filter(m=>!m.local&&!m.pending&&!m.failed).map(m=>String(m.id))});
    setMessages(prev=>{
      const u=[...prev,msg];
      persistThread(friend.email,u);
      lastMsgTsRef.current=now;
      return u;
    });
    activeSends.add(sendKey(email,friend.email,id));
    const req=imageData
      ?SocialAPI.sendDMImage(email,friend.email,imageData,displayName)
      :SocialAPI.sendDM(email,friend.email,text,displayName);
    Promise.resolve(req).then(r=>finishSend(id,r)).catch(()=>finishSend(id,null));
    scrollToBottom(true);
  };

  const send=()=>{
    if(composerLockRef.current||!accountIsCurrent())return;
    const text=input.trim();
    const img=imgPreview;
    if(!text&&!img)return;
    composerLockRef.current=true;
    Haptic.light();
    setInput("");
    setImgPreview(null);
    if(img)enqueueSend({imageData:img});
    if(text)enqueueSend({text});
    setTimeout(()=>{composerLockRef.current=false;},0);
  };

  const retrySend=(m)=>{
    const current=messagesRef.current.find(x=>x.id===m.id);
    if(!accountIsCurrent()||!current||(current.status!=="failed"&&!current.failed))return;
    const localId=m.localId||m.id;
    const key=sendKey(email,friend.email,localId);
    if(activeSends.has(key))return;
    activeSends.add(key);
    const now=new Date().toISOString();
    const baselineServerIDs=messagesRef.current.filter(x=>!x.local&&!x.pending&&!x.failed).map(x=>String(x.id));
    setMessages(prev=>{
      const next=prev.map(x=>x.id===m.id||x.localId===localId?{...x,status:"pending",pending:true,failed:false,ts:now,baselineServerIDs}:x);
      persistThread(friend.email,next);
      return next;
    });
    const isImg=!!m.imageData&&(!m.text||m.text==="📷 Photo");
    const req=isImg
      ?SocialAPI.sendDMImage(email,friend.email,m.imageData,displayName)
      :SocialAPI.sendDM(email,friend.email,m.text,displayName);
    Promise.resolve(req).then(r=>finishSend(localId,r)).catch(()=>finishSend(localId,null));
  };

  const handleInput=(v)=>setInput(v);

  const addReaction=(msgId,emoji)=>{
    const updated={...reactions,[msgId]:emoji};
    setReactions(updated);LS.set(`ft-reactions-${friend.email}`,updated);
    setReacting(null);
  };

  // Bug 11: dismiss reaction picker on outside tap
  useEffect(()=>{
    if(!reacting)return;
    const dismiss=(e)=>{
      if(!e.target.closest("[data-reaction-bubble]"))setReacting(null);
    };
    document.addEventListener("pointerdown",dismiss);
    return()=>document.removeEventListener("pointerdown",dismiss);
  },[reacting]);

  // Group messages: consecutive same-sender messages form a group
  // Insert date separators between groups on different days
  const buildGroups=()=>{
    const out=[];
    let lastDate="";
    let curGroup=null;
    messages.forEach((m,i)=>{
      const d2=new Date(m.ts);
      const dateKey=d2.toDateString();
      if(dateKey!==lastDate){
        if(curGroup)out.push(curGroup);
        curGroup=null;
        const now=new Date();
        const todayKey=now.toDateString();
        const yesterdayKey=new Date(now-86400000).toDateString();
        const diff=(now-d2)/86400000;
        let label=dateKey===todayKey?"Today":dateKey===yesterdayKey?"Yesterday":diff<7?d2.toLocaleDateString([],{weekday:"long"}):d2.toLocaleDateString([],{month:"short",day:"numeric",year:"numeric"});
        out.push({type:"date",label,key:"d-"+dateKey});
        lastDate=dateKey;
      }
      const isOwn=m.isOwn||m.from===email;
      if(!curGroup||curGroup.isOwn!==isOwn){
        if(curGroup)out.push(curGroup);
        curGroup={type:"group",isOwn,msgs:[m],key:"g-"+(m.id||i)};
      } else {
        curGroup.msgs.push(m);
      }
    });
    if(curGroup)out.push(curGroup);
    return out;
  };
  const groups=useMemo(()=>buildGroups(),[messages,email]);
  const lastOwnMsg=useMemo(()=>messages.filter(m=>m.isOwn||m.from===email).slice(-1)[0],[messages,email]);

  const quickMsgs=["💪 Nice PR!","🔥 Let's train","⏰ Don't skip today","👏 Keep it up","🏋️ Gym time?","🤝 You've got this"];
  const emojiReacts=["❤️","💪","🔥","😂","👍","😮"];

  // Bug 6: stabilize FriendAvatar with useMemo so React doesn't unmount on every render
  const FriendAvatar=useMemo(()=>({size=28})=>(
    <div style={{width:size,height:size,borderRadius:size/2,overflow:"hidden",flexShrink:0,
      background:friend.avatar?"none":`linear-gradient(135deg,${V.purple},#ec4899)`,
      display:"flex",alignItems:"center",justifyContent:"center",alignSelf:"flex-end"}}>
      {friend.avatar
        ?<img src={friend.avatar} style={{width:"100%",height:"100%",objectFit:"cover"}} alt=""/>
        :<span style={{fontSize:size*0.4,color:"#fff",fontWeight:800}}>{(friend.name||"?")[0].toUpperCase()}</span>}
    </div>
  ),[friend.avatar,friend.name]);

  return(
    <div style={{display:"flex",flexDirection:"column",height:"calc(var(--content-h, 100vh - 100px))"}}>
      {/* ── Header ── */}
      <div style={{display:"flex",alignItems:"center",gap:10,paddingBottom:10,borderBottom:`1px solid ${V.cardBorder}`,flexShrink:0}}>
        <button onClick={onBack} aria-label="Back to messages" style={{background:"none",border:"none",display:"flex",alignItems:"center",gap:2,cursor:"pointer",padding:"4px 0",flexShrink:0}}>
          <svg width="9" height="16" viewBox="0 0 9 16" fill="none"><path d="M8 1L1 8L8 15" stroke={V.accent} strokeWidth="2.2" strokeLinecap="round"/></svg>
          {/* Unread count badge on back arrow */}
        </button>
        {/* Tappable avatar/name — opens friend info sheet (Bug 3 fix: was calling onBack) */}
        <button onClick={()=>setFriendInfo(true)} style={{flex:1,display:"flex",alignItems:"center",gap:10,background:"none",border:"none",cursor:"pointer",textAlign:"left",padding:0}}>
          <div style={{width:36,height:36,borderRadius:18,overflow:"hidden",flexShrink:0,
            background:friend.avatar?"none":`linear-gradient(135deg,${V.purple},#ec4899)`,
            display:"flex",alignItems:"center",justifyContent:"center"}}>
            {friend.avatar
              ?<img src={friend.avatar} style={{width:"100%",height:"100%",objectFit:"cover"}} alt=""/>
              :<span style={{fontSize:14,color:"#fff",fontWeight:800}}>{(friend.name||"?")[0].toUpperCase()}</span>}
          </div>
          <div>
            <div style={{fontSize:15,fontWeight:700,color:V.text,lineHeight:1.2}}>{friendDisplayName(friend)}</div>
            <OnlineIndicator lastActive={friend.lastActive} showText/>
          </div>
        </button>
        {/* Video call placeholder — visual parity with iMessage */}
        <button style={{background:"none",border:"none",cursor:"default",padding:"4px 6px",opacity:0.35}}>
          <svg width="22" height="16" viewBox="0 0 24 18" fill="none" stroke={V.accent} strokeWidth="2" strokeLinecap="round"><rect x="1" y="2" width="15" height="14" rx="2"/><path d="M16 7l6-4v12l-6-4"/></svg>
        </button>
        <button onClick={()=>{setSearchOpen(!searchOpen);setSearchQ("");}} style={{background:"none",border:"none",cursor:"pointer",padding:"4px 6px",opacity:searchOpen?1:0.6}}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={searchOpen?V.accent:V.text3} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
        </button>
      </div>
      {/* ── Message search bar ── */}
      {searchOpen&&<div style={{padding:"6px 0",borderBottom:`1px solid ${V.cardBorder}`}}>
        <input value={searchQ} onChange={e=>setSearchQ(e.target.value)} autoFocus placeholder="Search messages..."
          style={{width:"100%",padding:"8px 12px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,
            borderRadius:8,color:V.text,fontSize:12,outline:"none",fontFamily:V.font,boxSizing:"border-box"}}/>
        {searchQ&&<div style={{fontSize:11,color:V.text3,marginTop:4}}>{messages.filter(m=>(m.text||"").toLowerCase().includes(searchQ.toLowerCase())).length} matches</div>}
      </div>}

      {/* ── Message list ── */}
      <div ref={listRef} style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch",display:"flex",flexDirection:"column",
        padding:"8px 0",gap:0}}>

        {/* Load older */}
        {hasMore&&(
          <div style={{textAlign:"center",padding:"6px 0 10px"}}>
            <button onClick={loadOlder} disabled={loadingOlder} style={{background:"none",border:"none",
              fontSize:12,color:V.accent,cursor:loadingOlder?"default":"pointer",padding:"4px 12px",fontFamily:V.font}}>
              {loadingOlder?"Loading…":"↑ Load older"}
            </button>
          </div>
        )}

        {loading&&messages.length===0&&(
          <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:40,marginBottom:8}}>💬</div>
              <div style={{fontSize:12,color:V.text3}}>Loading messages…</div>
            </div>
          </div>
        )}

        {!loading&&loadError&&messages.length===0&&(
          <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{textAlign:"center",padding:"0 32px"}}>
              <div style={{fontSize:40,marginBottom:8}}>⚠️</div>
              <div style={{fontSize:14,fontWeight:700,color:V.text,marginBottom:4}}>Couldn't load messages</div>
              <div style={{fontSize:12,color:V.text3,marginBottom:12}}>Check your connection and try again</div>
              <button onClick={loadMessages} style={{padding:"10px 24px",background:V.accent,border:"none",
                borderRadius:20,cursor:"pointer",fontSize:13,fontWeight:700,color:V.bg,fontFamily:V.font}}>Retry</button>
            </div>
          </div>
        )}

        {!loading&&!loadError&&messages.length===0&&(
          <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{textAlign:"center",padding:"0 32px"}}>
              <div style={{width:72,height:72,borderRadius:36,overflow:"hidden",
                background:friend.avatar?"none":`linear-gradient(135deg,${V.purple},#ec4899)`,
                display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px"}}>
                {friend.avatar
                  ?<img src={friend.avatar} style={{width:"100%",height:"100%",objectFit:"cover"}} alt=""/>
                  :<span style={{fontSize:28,color:"#fff",fontWeight:800}}>{(friend.name||"?")[0].toUpperCase()}</span>}
              </div>
              <div style={{fontSize:16,fontWeight:700,color:V.text,marginBottom:4}}>{friendDisplayName(friend)}</div>
              {friend.username&&<div style={{fontSize:12,color:V.text3,marginBottom:12}}>@{friend.username}</div>}
              <div style={{fontSize:12,color:V.text3}}>Say hi 👋</div>
            </div>
          </div>
        )}

        {groups.map(g=>{
          if(g.type==="date")return(
            <div key={g.key} style={{textAlign:"center",padding:"10px 0 6px"}}>
              <span style={{fontSize:11,color:V.text3,fontWeight:600,background:V.bg,padding:"2px 10px",borderRadius:8}}>{g.label}</span>
            </div>
          );

          const isOwn=g.isOwn;
          return(
            <div key={g.key} style={{display:"flex",flexDirection:"column",gap:2,
              alignItems:isOwn?"flex-end":"flex-start",padding:"2px 12px",marginBottom:4}}>
              {/* For friend messages: avatar only next to LAST message in group */}
              {g.msgs.map((m,mi)=>{
                const isLast=mi===g.msgs.length-1;
                const isFirst=mi===0;
                const reaction=reactions[m.id];
                // Bubble radius: iMessage groups: first/last have different corners
                const br=isOwn
                  ?{borderTopLeftRadius:18,borderTopRightRadius:isFirst?18:6,borderBottomRightRadius:isLast?4:6,borderBottomLeftRadius:18}
                  :{borderTopLeftRadius:isFirst?18:6,borderTopRightRadius:18,borderBottomRightRadius:18,borderBottomLeftRadius:isLast?4:6};
                const showAvatar=!isOwn&&isLast;
                const isLastOwn=lastOwnMsg&&m.id===lastOwnMsg.id;

                return(
                  <div key={m.id||mi} style={{display:"flex",alignItems:"flex-end",gap:6,
                    flexDirection:isOwn?"row-reverse":"row",maxWidth:"82%"}}>
                    {/* Avatar slot */}
                    {!isOwn&&(showAvatar?<FriendAvatar size={28}/>:<div style={{width:28,flexShrink:0}}/>)}

                    <div style={{display:"flex",flexDirection:"column",alignItems:isOwn?"flex-end":"flex-start"}}>
                      {/* Long-press → reaction picker (tap on mobile = onDoubleClick fallback) */}
                      <div onDoubleClick={()=>setReacting(reacting===m.id?null:m.id)}
                        data-reaction-bubble="1"
                        style={{position:"relative"}}>
                        {/* Bubble */}
                        {(m.imageData||m.gifUrl)?(
                          <div style={{...br,overflow:"hidden",position:"relative",cursor:"pointer",opacity:m.status==="pending"?0.7:1}}>
                            <img src={m.imageData||m.gifUrl} alt={m.gifUrl?"GIF":"Photo"}
                              style={{display:"block",maxWidth:220,maxHeight:260,objectFit:"cover"}}/>
                            {/* Time overlay on image — no checkmarks, Read/Delivered shows below like text bubbles */}
                            <div style={{position:"absolute",bottom:6,right:8,fontSize:11,
                              color:"rgba(255,255,255,0.9)",background:"rgba(0,0,0,0.4)",
                              borderRadius:6,padding:"2px 6px"}}>
                              {new Date(m.ts).toLocaleTimeString([],{hour:"numeric",minute:"2-digit"})}
                            </div>
                          </div>
                        ):(
                          <div style={{
                            ...br,
                            padding:"8px 13px",
                            background:isOwn?V.accent:V.hoverBg,
                            border:isOwn?"none":`1px solid ${V.cardBorder}`,
                            cursor:"default",
                            opacity:m.status==="pending"?0.7:1,
                          }}>
                            <div style={{fontSize:15,color:isOwn?V.bg:V.text,lineHeight:1.4,wordBreak:"break-word",
                              background:searchQ&&(m.text||"").toLowerCase().includes(searchQ.toLowerCase())?`${V.accent}30`:undefined,
                              borderRadius:searchQ?4:0}}>{m.text}</div>
                          </div>
                        )}
                        {/* Reaction pill */}
                        {reaction&&(
                          <div style={{position:"absolute",bottom:-10,right:isOwn?4:"auto",left:isOwn?"auto":4,
                            background:V.card,border:`1px solid ${V.cardBorder}`,borderRadius:10,
                            padding:"1px 6px",fontSize:13,zIndex:2}}>{reaction}</div>
                        )}
                        {/* Reaction picker popover */}
                        {reacting===m.id&&(
                          <div style={{position:"absolute",bottom:"calc(100% + 8px)",
                            right:isOwn?0:"auto",left:isOwn?"auto":0,
                            background:V.card,border:`1px solid ${V.cardBorder}`,borderRadius:20,
                            padding:"6px 10px",display:"flex",gap:6,zIndex:10,
                            boxShadow:"0 8px 24px rgba(0,0,0,0.4)"}}>
                            {emojiReacts.map(e=>(
                              <button key={e} onClick={()=>addReaction(m.id,e)} style={{background:"none",border:"none",
                                fontSize:20,cursor:"pointer",padding:"2px",borderRadius:8,
                                WebkitTapHighlightColor:"transparent"}}>{e}</button>
                            ))}
                            {isOwn&&<button onClick={()=>{setReacting(null);ConfirmCtrl.show("Delete message?","This cannot be undone.",async()=>{
                              if(m.status==="pending"||m.pending)return;
                              if(m.status!=="failed"&&!m.failed){
                                const result=await SocialAPI.deleteMessage(email,m.id);
                                if(!result||result.error||result.success===false||result.ok===false){SuccessToastCtrl.error("Could not delete message. Try again.");return;}
                              }
                              if(!accountIsCurrent())return;
                              setMessages(prev=>prev.filter(x=>x.id!==m.id));
                              LS.set(`ft-dm-${friend.email}`,(LS.get(`ft-dm-${friend.email}`)||[]).filter(x=>x.id!==m.id));
                            });}} style={{background:"none",border:"none",fontSize:16,cursor:"pointer",padding:"2px",borderRadius:8,
                              WebkitTapHighlightColor:"transparent",opacity:0.7}}>🗑️</button>}
                          </div>
                        )}
                      </div>
                      {/* Timestamp — only under last message in group (iMessage style) */}
                      {isLast&&(
                        <div style={{display:"flex",alignItems:"center",gap:4,marginTop:3,
                          justifyContent:isOwn?"flex-end":"flex-start",padding:"0 2px"}}>
                          <span style={{fontSize:11,color:V.text3}}>
                            {new Date(m.ts).toLocaleTimeString([],{hour:"numeric",minute:"2-digit"})}
                          </span>
                        </div>
                      )}
                      {isOwn&&(m.status==="failed"||m.failed)&&(
                        <button onClick={()=>retrySend(m)} style={{fontSize:11,color:V.danger,marginTop:1,padding:"0 2px",
                          background:"none",border:"none",cursor:"pointer",fontFamily:V.font}}>
                          Not sent · Retry
                        </button>
                      )}
                      {isOwn&&(m.status==="pending"||m.pending)&&(
                        <div style={{fontSize:11,color:V.text3,marginTop:1,padding:"0 2px"}}>Sending…</div>
                      )}
                      {isOwn&&isLastOwn&&isLast&&m.status!=="failed"&&m.status!=="pending"&&!m.failed&&!m.pending&&(
                        <div style={{fontSize:11,color:m.read?V.accent:V.text3,marginTop:1,padding:"0 2px",transition:"color .3s"}}>
                          {m.read?"Read":"Delivered"}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}

        <div ref={endRef}/>
      </div>

      {/* ── Quick replies ── */}
      <div style={{display:"flex",gap:6,overflowX:"auto",padding:"6px 12px 4px",
        WebkitOverflowScrolling:"touch",scrollbarWidth:"none",flexShrink:0}}>
        {quickMsgs.map((msg,i)=>(
          <button key={i} onClick={()=>{
            if(composerLockRef.current||!accountIsCurrent())return;
            composerLockRef.current=true;
            Haptic.light();
            enqueueSend({text:msg});
            setTimeout(()=>{composerLockRef.current=false;},0);
          }} style={{padding:"5px 11px",borderRadius:14,flexShrink:0,
            background:V.inputBg,border:`1px solid ${V.cardBorder}`,
            cursor:"pointer",fontSize:12,color:V.text3,fontFamily:V.font,whiteSpace:"nowrap"}}>
            {msg}
          </button>
        ))}
      </div>

      {/* ── Image preview ── */}
      {imgPreview&&(
        <div style={{padding:"0 12px 6px",flexShrink:0}}>
          <div style={{position:"relative",display:"inline-block"}}>
            <img src={imgPreview} alt="Preview" style={{maxHeight:90,maxWidth:160,borderRadius:10,display:"block",border:`2px solid ${V.accent}`}}/>
            <button onClick={()=>setImgPreview(null)} style={{position:"absolute",top:-6,right:-6,
              width:20,height:20,borderRadius:10,background:V.danger,border:`2px solid ${V.bg}`,
              cursor:"pointer",color:"#fff",fontSize:11,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:V.font}}>✕</button>
          </div>
        </div>
      )}

      {/* ── Push notification prompt ── */}
      {typeof Notification!=="undefined"&&Notification.permission==="default"&&(
        <div style={{display:"flex",alignItems:"center",gap:8,padding:"6px 12px",flexShrink:0,
          borderTop:`1px solid ${V.cardBorder}`}}>
          <span style={{fontSize:11,color:V.text2,flex:1}}>🔔 Enable notifications for message alerts</span>
          <button onClick={async()=>{
            const p=await Notification.requestPermission();
            if(p==="granted"&&"serviceWorker" in navigator){
              const reg=await navigator.serviceWorker.ready;
              const vapidKey="BMZyIUQO08nuR7afvqNIWBK_ZOcr7PHwT2YwIxTa_ONwAD1YCaJ8Qkb4q4TTYSx_sTN-7-5vCzR8ApNZuyg-Ttc";
              const keyBytes=Uint8Array.from(atob(vapidKey.replace(/-/g,"+").replace(/_/g,"/")),c=>c.charCodeAt(0));
              const sub=await reg.pushManager.subscribe({userVisibleOnly:true,applicationServerKey:keyBytes}).catch(()=>null);
              if(sub){await apiPost("/api/push",{action:"subscribe",email,subscription:sub.toJSON()},{email});SuccessToastCtrl.show("Notifications enabled 🔔");}
            }
          }} style={{padding:"5px 12px",borderRadius:12,background:V.accent,border:"none",cursor:"pointer",
            fontSize:11,fontWeight:700,color:V.bg,flexShrink:0,fontFamily:V.font}}>Enable</button>
        </div>
      )}

      {/* ── Input bar ── */}
      <div style={{display:"flex",alignItems:"flex-end",gap:8,padding:"8px 12px 12px",
        borderTop:`1px solid ${V.cardBorder}`,flexShrink:0,background:V.bg}}>
        {/* Camera / attach */}
        <input ref={imgRef} type="file" accept={"image/" + "*"} onChange={handleImg} style={{display:"none"}}/>
        <button onClick={()=>imgRef.current?.click()} style={{width:34,height:34,borderRadius:17,flexShrink:0,
          background:V.hoverBg,border:`1px solid ${V.cardBorder}`,cursor:"pointer",
          display:"flex",alignItems:"center",justifyContent:"center",marginBottom:2}}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={V.text3} strokeWidth="2" strokeLinecap="round">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
            <circle cx="12" cy="13" r="4"/>
          </svg>
        </button>

        {/* Text input — pill style */}
        <div style={{flex:1,position:"relative"}}>
          <textarea value={input} ref={inputRef}
            onChange={e=>{if(e.target.value.length<=1000)handleInput(e.target.value);}}
            onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
            placeholder="iMessage"
            rows={1}
            style={{width:"100%",padding:"9px 14px",background:V.hoverBg,
              border:`1px solid ${V.cardBorder}`,borderRadius:20,color:V.text,
              fontSize:15,outline:"none",fontFamily:V.font,boxSizing:"border-box",
              resize:"none",maxHeight:100,overflowY:"auto",lineHeight:1.4,
              WebkitOverflowScrolling:"touch",display:"block"}}/>
          {input.length>800&&(
            <span style={{position:"absolute",right:10,bottom:-14,fontSize:11,
              color:input.length>950?V.danger:V.text3}}>{input.length}/1000</span>
          )}
        </div>

        {/* Send / mic button */}
        <button onClick={send} aria-label="Send message" disabled={!input.trim()&&!imgPreview}
          style={{width:34,height:34,borderRadius:17,flexShrink:0,marginBottom:2,
            background:(input.trim()||imgPreview)?V.accent:V.hoverBg,
            border:"none",cursor:(input.trim()||imgPreview)?"pointer":"default",
            display:"flex",alignItems:"center",justifyContent:"center",transition:"background .15s"}}>
          {(input.trim()||imgPreview)
            ?<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={V.bg} strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
            :<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={V.text3} strokeWidth="2" strokeLinecap="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>}
        </button>
      </div>

      {/* Bug 3: Friend info sheet — slides up when header avatar/name tapped */}
      {friendInfo&&(
        <div style={{position:"fixed",inset:0,zIndex:80,display:"flex",flexDirection:"column",justifyContent:"flex-end"}}
          onClick={()=>setFriendInfo(false)}>
          <div style={{background:V.bg,border:`1px solid ${V.cardBorder}`,borderRadius:"20px 20px 0 0",
            padding:"12px 20px 32px",maxHeight:"80vh",overflowY:"auto"}}
            onClick={e=>e.stopPropagation()}>
            {/* Handle bar */}
            <div style={{width:36,height:4,borderRadius:2,background:track(),margin:"0 auto 16px"}}/>
            {/* Avatar */}
            <div style={{textAlign:"center",marginBottom:16}}>
              <div style={{width:80,height:80,borderRadius:40,overflow:"hidden",
                background:friend.avatar?"none":`linear-gradient(135deg,${V.purple},#ec4899)`,
                display:"inline-flex",alignItems:"center",justifyContent:"center",marginBottom:8}}>
                {friend.avatar
                  ?<img src={friend.avatar} style={{width:"100%",height:"100%",objectFit:"cover"}} alt=""/>
                  :<span style={{fontSize:32,color:"#fff",fontWeight:800}}>{(friend.name||"?")[0].toUpperCase()}</span>}
              </div>
              <div style={{fontSize:18,fontWeight:800,color:V.text}}>{friendDisplayName(friend)}</div>
              {friend.username&&<div style={{fontSize:13,color:V.text3,marginTop:2}}>@{friend.username}</div>}
              {friend.bio&&<div style={{fontSize:13,color:V.text2,marginTop:8,lineHeight:1.5,fontStyle:"italic"}}>{friend.bio}</div>}
            </div>
            {/* Quick actions */}
            <div style={{display:"flex",gap:12,justifyContent:"center",marginBottom:20}}>
              {[
                {icon:"📞",label:"Call",action:()=>{}},
                {icon:"📹",label:"Video",action:()=>{}},
                {icon:"🔔",label:"Mute",action:()=>{}},
                {icon:"🔍",label:"Search",action:()=>{}},
              ].map(btn=>(
                <button key={btn.label} onClick={btn.action} style={{display:"flex",flexDirection:"column",alignItems:"center",
                  gap:4,background:V.hoverBg,border:"none",borderRadius:12,
                  padding:"10px 16px",cursor:"pointer",minWidth:56}}>
                  <span style={{fontSize:20}}>{btn.icon}</span>
                  <span style={{fontSize:11,color:V.text3,fontWeight:600}}>{btn.label}</span>
                </button>
              ))}
            </div>
            {/* Info rows */}
            <div style={{display:"flex",flexDirection:"column",gap:1,background:V.card,borderRadius:12,overflow:"hidden"}}>
              {friend.challenges?.map((c,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"12px 16px",
                  borderBottom:`1px solid ${V.cardBorder}`}}>
                  <span style={{fontSize:13,color:V.text2,textTransform:"capitalize"}}>{c.challenge_id}</span>
                  <span style={{fontSize:13,fontWeight:700,color:V.accent,fontFamily:V.mono}}>{c.value}</span>
                </div>
              ))}
            </div>
            <button onClick={()=>setFriendInfo(false)} style={{width:"100%",marginTop:16,padding:"13px",
              background:V.inputBg,border:`1px solid ${V.cardBorder}`,borderRadius:12,
              cursor:"pointer",fontSize:14,fontWeight:600,color:V.text3,fontFamily:V.font}}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
